import { create } from "zustand";
import { Phrase, PhraseType } from "../../../api/phrasebook";

const usePhrasebookStore = create<{
  // job state
  jobId: string;
  batchId: string;
  setJobId: (jobId: string) => void;
  setBatchId: (batchId: string) => void;
  // phrases state
  savingPhrases: boolean;
  setSavingPhrases: (saving: boolean) => void;
  hasChanges: boolean;
  setHasChanges: (hasChanges: boolean) => void;
  // active field state
  activeField: {
    index: number;
    type: PhraseType;
  } | null;
  setActiveField: (field: { index: number; type: PhraseType } | null) => void;
  // search box state
  selectedBrandIds: string[] | null;
  setSelectedBrandIds: (brandIds: string[]) => void;
  matchSuitableFor: boolean;
  setMatchSuitableFor: (match: boolean) => void;
  searchTab: "phrases" | "jobs";
  setSearchTab: (tab: "phrases" | "jobs") => void;
  searchOptionTab: "exact" | "partial";
  setSearchOptionTab: (tab: "exact" | "partial") => void;
  query: string;
  setQuery: (query: string) => void;
  refreshSearch: boolean;
  setRefreshSearch: (refreshSearch: boolean) => void;
  garmentType: string;
  garmentTypeApproved: boolean;
  setGarmentType: (garmentType: string, approved?: boolean) => void;
  isGenerating:
    | false
    | {
        type:
          | "title"
          | "regularDescription"
          | "shortDescription"
          | "longDescription";
        ts: number;
      };
  setIsGenerating: (
    isGenerating:
      | false
      | {
          type:
            | "title"
            | "regularDescription"
            | "shortDescription"
            | "longDescription";
          ts: number;
        }
  ) => void;
  // events
  selectedPhrase: Phrase | null;
  setSelectedPhrase: (phrase: Phrase | null) => void;
  // generated content cache
  generatedContent: Record<string, string[]>;
  setGeneratedContent: (type: string, content: string[]) => void;
  clearGeneratedContent: (type?: string) => void;
  // centralized actions
  startPhrasebookSearch: (
    field: { index: number; type: PhraseType },
    query?: string,
    garmentType?: string
  ) => void;
  startGeneration: (
    type:
      | "title"
      | "regularDescription"
      | "shortDescription"
      | "longDescription"
  ) => void;
  startKeywordSearch: (query?: string) => void;
  clearSearch: () => void;
}>((set) => ({
  selectedBrandIds: null,
  setSelectedBrandIds: (brandIds: string[] | null) =>
    set({ selectedBrandIds: brandIds }),
  matchSuitableFor: true,
  setMatchSuitableFor: (match: boolean) => set({ matchSuitableFor: match }),
  jobId: "",
  batchId: "",
  setJobId: (jobId: string) => set({ jobId }),
  setBatchId: (batchId: string) => set({ batchId }),
  savingPhrases: false,
  setSavingPhrases: (saving: boolean) => set({ savingPhrases: saving }),
  hasChanges: false,
  setHasChanges: (hasChanges: boolean) => set({ hasChanges }),
  activeField: null,
  setActiveField: (field: { index: number; type: PhraseType } | null) =>
    set({ activeField: field }),
  searchTab: "phrases",
  setSearchTab: (tab: "phrases" | "jobs") => set({ searchTab: tab }),
  searchOptionTab: "exact",
  setSearchOptionTab: (tab: "exact" | "partial") =>
    set({ searchOptionTab: tab }),
  query: "",
  setQuery: (query: string) => set({ query }),
  refreshSearch: false,
  setRefreshSearch: (refreshSearch: boolean) => set({ refreshSearch }),
  garmentType: "",
  garmentTypeApproved: false,
  setGarmentType: (garmentType: string, approved: boolean = false) =>
    set({ garmentType, garmentTypeApproved: approved }),
  isGenerating: false,
  setIsGenerating: (
    isGenerating:
      | false
      | {
          type:
            | "title"
            | "regularDescription"
            | "shortDescription"
            | "longDescription";
          ts: number;
        }
  ) => set({ isGenerating }),
  selectedPhrase: null,
  setSelectedPhrase: (phrase: Phrase | null) => set({ selectedPhrase: phrase }),
  // generated content cache implementation
  generatedContent: {},
  setGeneratedContent: (type: string, content: string[]) =>
    set((state) => ({
      generatedContent: { ...state.generatedContent, [type]: content },
    })),
  clearGeneratedContent: (type?: string) =>
    set((state) => {
      if (type) {
        const newGeneratedContent = { ...state.generatedContent };
        delete newGeneratedContent[type];
        return { generatedContent: newGeneratedContent };
      }
      return { generatedContent: {} };
    }),
  // centralized actions implementation
  startPhrasebookSearch: (
    field?: { index: number; type: PhraseType },
    query?: string,
    garmentType?: string
  ) =>
    set({
      activeField: field || null,
      searchTab: "phrases",
      query: query || garmentType || "",
      isGenerating: false,
    }),
  startGeneration: (
    type:
      | "title"
      | "regularDescription"
      | "shortDescription"
      | "longDescription"
  ) => set({ isGenerating: { type, ts: Date.now() } }),
  startKeywordSearch: (query?: string) =>
    set({
      query: query || "",
      searchTab: "jobs",
      activeField: null,
      isGenerating: false,
    }),
  clearSearch: () =>
    set({
      activeField: null,
      query: "",
      searchTab: "phrases",
      isGenerating: false,
    }),
}));

export default usePhrasebookStore;
